import { type ReactNode, type RefObject, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ActionList } from '@m/alchemy-ui/ActionList';
import { IconButton } from '@m/alchemy-ui/IconButton';
import { Popper } from '@m/alchemy-ui/Popper';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { DownloadIcon } from '@m/alchemy-ui/icons/DownloadIcon';
import { FileTypeExcelIcon } from '@m/alchemy-ui/icons/FileTypeExcelIcon';
import { FileTypeGenericIcon } from '@m/alchemy-ui/icons/FileTypeGenericIcon';
import { FileTypeImageIcon } from '@m/alchemy-ui/icons/FileTypeImageIcon';
import { PrintIcon } from '@m/alchemy-ui/icons/PrintIcon';
import { type ColorScheme } from '@m/alchemy-ui/theme';
import { color } from '@m/alchemy-theme';
import styled from 'styled-components';
import {
  type ExportCell,
  type ExportColumns,
  exportCsv,
  exportImage,
  exportSvg,
  exportXls,
  ImageFormat,
  printChart
} from '../../../common/chart-export';

interface ChartExportMenuProps {
  chartRef: RefObject<HTMLElement>;
  fileName: string;
  formatDate?: (date: Date) => string;
  getExportData?: () => { columns: ExportColumns[]; rows: ExportCell[][] };
}

interface ChartExportMenuAction {
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
}

interface ChartExportMenuActionGroup {
  label: string;
  actions: ChartExportMenuAction[];
}

type ChartExportMenuItem = ChartExportMenuAction | ChartExportMenuActionGroup;

const DEFAULT_FILE_NAME = 'chart';

const IconWrap = styled.span<{ $palette: keyof ColorScheme }>`
  display: inline-flex;
  color: ${({ $palette }) => color('base', { palette: $palette })};

  svg {
    color: ${({ $palette }) => color('base', { palette: $palette })} !important;
  }
`;

const sanitizeFileName = (name: string) => {
  const sanitized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return sanitized || DEFAULT_FILE_NAME;
};

const formatDateSuffix = (date: Date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const ChartExportMenu = ({ chartRef, fileName, formatDate, getExportData }: ChartExportMenuProps) => {
  const intl = useIntl();
  const resolvedFileName = useMemo(() => `${sanitizeFileName(fileName)}_${formatDateSuffix(new Date())}`, [fileName]);

  const resolveExportData = useCallback(() => {
    return getExportData?.() || { columns: [], rows: [] };
  }, [getExportData]);

  const handleExportCsv = useCallback(() => {
    const exportData = resolveExportData();
    exportCsv({ columns: exportData.columns, rows: exportData.rows, formatDate, fileName: resolvedFileName });
  }, [formatDate, resolvedFileName, resolveExportData]);

  const handleExportXls = useCallback(() => {
    const exportData = resolveExportData();
    exportXls({ columns: exportData.columns, rows: exportData.rows, formatDate, fileName: resolvedFileName });
  }, [formatDate, resolvedFileName, resolveExportData]);

  const handleExportImage = useCallback(
    async (format: ImageFormat) => {
      await exportImage({ container: chartRef.current, fileName: resolvedFileName, format });
    },
    [chartRef, resolvedFileName]
  );

  const handleExportSvg = useCallback(() => {
    exportSvg({ container: chartRef.current, fileName: resolvedFileName });
  }, [chartRef, resolvedFileName]);

  const handlePrintChart = useCallback(async () => {
    await printChart({ container: chartRef.current, fileName: resolvedFileName });
  }, [chartRef, resolvedFileName]);

  const actions: ChartExportMenuItem[] = useMemo(
    () => [
      {
        label: intl.formatMessage({ id: 'chartExport.groupExportChart', defaultMessage: 'Export chart' }),
        actions: [
          {
            label: 'PNG',
            icon: (
              <IconWrap $palette="expressivePositive">
                <FileTypeImageIcon size={16} aria-hidden="true" />
              </IconWrap>
            ),
            onClick: () => {
              handleExportImage(ImageFormat.Png);
            }
          },
          {
            label: 'JPEG',
            icon: (
              <IconWrap $palette="expressiveWarning">
                <FileTypeImageIcon size={16} aria-hidden="true" />
              </IconWrap>
            ),
            onClick: () => {
              handleExportImage(ImageFormat.Jpeg);
            }
          },
          {
            label: 'SVG',
            icon: (
              <IconWrap $palette="expressiveNeutral">
                <FileTypeGenericIcon size={16} aria-hidden="true" />
              </IconWrap>
            ),
            onClick: handleExportSvg
          }
        ]
      },
      {
        label: intl.formatMessage({ id: 'chartExport.groupExportData', defaultMessage: 'Export chart data' }),
        actions: [
          {
            label: 'CSV',
            icon: (
              <IconWrap $palette="expressiveInformative">
                <FileTypeGenericIcon size={16} aria-hidden="true" />
              </IconWrap>
            ),
            onClick: handleExportCsv
          },
          {
            label: 'XLS',
            icon: (
              <IconWrap $palette="interactiveConstructive">
                <FileTypeExcelIcon size={16} aria-hidden="true" />
              </IconWrap>
            ),
            onClick: handleExportXls
          }
        ]
      },
      {
        label: intl.formatMessage({ id: 'chartExport.groupOther', defaultMessage: 'Others' }),
        actions: [
          {
            label: intl.formatMessage({ id: 'chartExport.print', defaultMessage: 'Print chart' }),
            icon: (
              <IconWrap $palette="interactiveNavigational">
                <PrintIcon size={16} aria-hidden="true" />
              </IconWrap>
            ),
            onClick: () => {
              handlePrintChart().catch(() => {});
            }
          }
        ]
      }
    ],
    [handleExportCsv, handleExportImage, handleExportSvg, handleExportXls, handlePrintChart, intl]
  );

  return (
    <Popper
      placement="bottom"
      trigger={
        <IconButton
          icon={<DownloadIcon aria-hidden="true" size={24} />}
          aria-label={intl.formatMessage({ id: 'chartExport.menuLabel', defaultMessage: 'Chart export' })}
          data-testid="chart-export-menu"
        />
      }
      overlay={({ closeAndReturnFocus }) => (
        <PopperBox hasPadding={false} hasArrow>
          <ActionList
            shouldAutoFocusMenu={false}
            actionsHaveIcons
            actions={actions.map((action) => {
              const wrapAction = (item: ChartExportMenuAction) => ({
                ...item,
                onClick: () => {
                  item.onClick?.();
                  closeAndReturnFocus();
                }
              });

              return 'actions' in action
                ? {
                    ...action,
                    actions: action.actions.map(wrapAction)
                  }
                : wrapAction(action);
            })}
          />
        </PopperBox>
      )}
    />
  );
};
